import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Slider, Chip, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Tooltip, TextField, Switch, FormControlLabel,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter, CartesianGrid, ReferenceLine,
} from 'recharts';
import { bloomberg } from '../theme/bloombergTheme';
import {
  getDcf, recomputeDcf, getReverseDcf, getMultiplesTarget,
  getPeerRegression, recomputePeerRegression, getGrowthDcf, recomputeGrowthDcf,
  DcfResult, DcfOverrides, ReverseDcfResult, MultiplesTargetResult,
  PeerRegressionResult, GrowthDcfResult, GrowthDcfOverrides, TerminalValueBreakdown,
} from '../services/api';

// ──────────────────────────────────────────────────────────────────────────
// ValuationTab — interactive 2-stage DCF + Reverse DCF.
//
// Backend does all math (research.compute_dcf, inputs cached 15 min server-
// side so slider recomputes are ~100ms). This component:
//   • loads the base case + derived assumptions
//   • lets the user override growth / terminal / WACC / base FCF via sliders
//   • debounce-recomputes on change
//   • Reverse mode: solves for the growth the market is pricing in
//   • Bear/Base/Bull scenarios persisted per-symbol in localStorage
// ──────────────────────────────────────────────────────────────────────────

interface Props {
  symbol: string;
  currencySymbol: string;
}

type Mode = 'dcf' | 'reverse' | 'growth' | 'relative';
type ScenarioName = 'bear' | 'base' | 'bull';

const scenarioKey = (sym: string) => `dcf_scenarios_${sym}`;

const pct = (v: number | null | undefined, dp = 1) =>
  v == null ? '—' : `${(v * 100).toFixed(dp)}%`;

const SLIDER_SX = {
  color: bloomberg.accent.orange,
  '& .MuiSlider-rail': { bgcolor: bloomberg.bg.tertiary },
  '& .MuiSlider-valueLabel': { fontSize: '0.65rem', bgcolor: bloomberg.bg.tertiary },
};

const CARD_SX = {
  p: 2,
  bgcolor: bloomberg.bg.secondary,
  border: `1px solid ${bloomberg.border.primary}`,
  borderRadius: 1,
};

const ValuationTab: React.FC<Props> = ({ symbol, currencySymbol }) => {
  const [mode, setMode] = useState<Mode>('dcf');
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DcfResult | null>(null);
  const [reverse, setReverse] = useState<ReverseDcfResult | null>(null);
  const [reverseLoading, setReverseLoading] = useState(false);

  // Multiples-implied target — independent of the DCF sliders (own historical
  // multiple × forward estimate), so it's fetched once per symbol, not
  // recomputed on every assumption change.
  const [multiples, setMultiples] = useState<MultiplesTargetResult | null>(null);
  const [multiplesLoading, setMultiplesLoading] = useState(true);

  // Editable assumptions (raw decimals). null = not yet initialised.
  const [g1, setG1] = useState<number | null>(null);
  const [tg, setTg] = useState<number | null>(null);
  const [waccOverrideOn, setWaccOverrideOn] = useState(false);
  const [wacc, setWacc] = useState<number | null>(null);
  const [baseFcfB, setBaseFcfB] = useState<string>('');  // billions, as text input

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const multiplesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baseAssumptions = useRef<DcfResult['assumptions'] | null>(null);

  // ── Growth DCF (Damodaran-style: peer-margin convergence + sales-to-capital
  // reinvestment) — independent engine, own state, lazy-loaded only when the
  // user switches to this mode (avoids extra yfinance/peer-batch calls on
  // every page load). ──
  const [growthResult, setGrowthResult] = useState<GrowthDcfResult | null>(null);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [growthRecomputing, setGrowthRecomputing] = useState(false);
  const [growthError, setGrowthError] = useState<string | null>(null);
  const [ggGrowth, setGgGrowth] = useState<number | null>(null);
  const [ggTermGrowth, setGgTermGrowth] = useState<number | null>(null);
  const [ggTargetMargin, setGgTargetMargin] = useState<number | null>(null);
  const [ggSalesToCapital, setGgSalesToCapital] = useState<number | null>(null);
  const [ggConvergenceYears, setGgConvergenceYears] = useState<number>(10);
  const [ggWaccOverrideOn, setGgWaccOverrideOn] = useState(false);
  const [ggWacc, setGgWacc] = useState<number | null>(null);
  const growthBaseAssumptions = useRef<GrowthDcfResult['assumptions'] | null>(null);
  const growthDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const growthFetchedRef = useRef(false);

  useEffect(() => {
    if (mode !== 'growth' || growthFetchedRef.current) return;
    growthFetchedRef.current = true;
    setGrowthLoading(true);
    setGrowthError(null);
    getGrowthDcf(symbol)
      .then(d => {
        growthBaseAssumptions.current = d.assumptions;
        setGrowthResult(d);
        setGgGrowth(d.assumptions.stage1_growth);
        setGgTermGrowth(d.assumptions.terminal_growth);
        setGgTargetMargin(d.assumptions.target_operating_margin);
        setGgSalesToCapital(d.assumptions.sales_to_capital);
        setGgConvergenceYears(d.assumptions.margin_convergence_years);
        setGgWacc(d.assumptions.wacc);
        setGgWaccOverrideOn(false);
      })
      .catch(e => setGrowthError(e?.response?.data?.detail || `Growth DCF not available for ${symbol}`))
      .finally(() => setGrowthLoading(false));
  }, [mode, symbol]);

  const growthOverrides = useCallback((): GrowthDcfOverrides => {
    const base = growthBaseAssumptions.current;
    const ov: GrowthDcfOverrides = {};
    if (!base) return ov;
    if (ggGrowth != null && Math.abs(ggGrowth - base.stage1_growth) > 1e-6) ov.stage1_growth = ggGrowth;
    if (ggTermGrowth != null && Math.abs(ggTermGrowth - base.terminal_growth) > 1e-6) ov.terminal_growth = ggTermGrowth;
    if (ggTargetMargin != null && Math.abs(ggTargetMargin - base.target_operating_margin) > 1e-6) ov.target_operating_margin = ggTargetMargin;
    if (ggSalesToCapital != null && Math.abs(ggSalesToCapital - base.sales_to_capital) > 1e-6) ov.sales_to_capital = ggSalesToCapital;
    if (ggConvergenceYears !== base.margin_convergence_years) ov.margin_convergence_years = ggConvergenceYears;
    if (ggWaccOverrideOn && ggWacc != null) ov.wacc = ggWacc;
    return ov;
  }, [ggGrowth, ggTermGrowth, ggTargetMargin, ggSalesToCapital, ggConvergenceYears, ggWaccOverrideOn, ggWacc]);

  useEffect(() => {
    if (mode !== 'growth' || !growthBaseAssumptions.current) return;
    if (growthDebounceRef.current) clearTimeout(growthDebounceRef.current);
    growthDebounceRef.current = setTimeout(() => {
      setGrowthRecomputing(true);
      recomputeGrowthDcf(symbol, growthOverrides())
        .then(d => setGrowthResult(d))
        .catch(() => {})
        .finally(() => setGrowthRecomputing(false));
    }, 250);
    return () => { if (growthDebounceRef.current) clearTimeout(growthDebounceRef.current); };
    // eslint-disable-next-line
  }, [mode, ggGrowth, ggTermGrowth, ggTargetMargin, ggSalesToCapital, ggConvergenceYears, ggWaccOverrideOn, ggWacc]);

  const resetGrowthToBase = () => {
    const b = growthBaseAssumptions.current;
    if (!b) return;
    setGgGrowth(b.stage1_growth); setGgTermGrowth(b.terminal_growth);
    setGgTargetMargin(b.target_operating_margin); setGgSalesToCapital(b.sales_to_capital);
    setGgConvergenceYears(b.margin_convergence_years);
    setGgWaccOverrideOn(false); setGgWacc(b.wacc);
  };

  // ── Peer-Multiple Regression — EV/Revenue vs. forward growth across a peer
  // group. Own state, lazy-loaded only on first visit to this mode. ──
  const [peerResult, setPeerResult] = useState<PeerRegressionResult | null>(null);
  const [peerLoading, setPeerLoading] = useState(false);
  const [peerRecomputing, setPeerRecomputing] = useState(false);
  const [peerError, setPeerError] = useState<string | null>(null);
  const [peerGrowthPct, setPeerGrowthPct] = useState<number | null>(null);  // percent, for the slider
  const peerFetchedRef = useRef(false);
  const peerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (mode !== 'relative' || peerFetchedRef.current) return;
    peerFetchedRef.current = true;
    setPeerLoading(true);
    setPeerError(null);
    getPeerRegression(symbol)
      .then(d => {
        setPeerResult(d);
        if (!d.insufficient_peer_data) setPeerGrowthPct(d.target_growth * 100);
      })
      .catch(e => setPeerError(e?.response?.data?.detail || `Peer regression not available for ${symbol}`))
      .finally(() => setPeerLoading(false));
  }, [mode, symbol]);

  useEffect(() => {
    if (mode !== 'relative' || peerGrowthPct == null || !peerResult || peerResult.insufficient_peer_data) return;
    if (Math.abs(peerGrowthPct / 100 - peerResult.target_growth) < 1e-6) return;  // no-op, already matches
    if (peerDebounceRef.current) clearTimeout(peerDebounceRef.current);
    peerDebounceRef.current = setTimeout(() => {
      setPeerRecomputing(true);
      recomputePeerRegression(symbol, peerGrowthPct / 100)
        .then(d => setPeerResult(d))
        .catch(() => {})
        .finally(() => setPeerRecomputing(false));
    }, 250);
    return () => { if (peerDebounceRef.current) clearTimeout(peerDebounceRef.current); };
    // eslint-disable-next-line
  }, [mode, peerGrowthPct]);

  // ── Initial load: base case ──
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);
    setReverse(null);
    setActiveScenario(null);
    setG1(null);      // gates the multiples-target effect until the new symbol's growth is known
    setMultiples(null);
    // New symbol — allow the Growth DCF / Peer Regression modes to re-fetch
    // if the user (re-)visits them, and clear any stale prior-symbol output.
    growthFetchedRef.current = false;
    peerFetchedRef.current = false;
    growthBaseAssumptions.current = null;
    setGrowthResult(null);
    setGrowthError(null);
    setPeerResult(null);
    setPeerError(null);
    setPeerGrowthPct(null);
    getDcf(symbol)
      .then(d => {
        if (cancelled) return;
        setResult(d);
        if (!d.needs_target_margin) {
          baseAssumptions.current = d.assumptions;
          setG1(d.assumptions.stage1_growth);
          setTg(d.assumptions.terminal_growth);
          setWacc(d.assumptions.wacc);
          setWaccOverrideOn(false);
          setBaseFcfB((d.assumptions.base_fcf / 1e9).toFixed(1));
        }
      })
      .catch(e => !cancelled && setError(e?.response?.data?.detail || `DCF not available for ${symbol}`))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [symbol]);

  // ── "Needs a target margin" assumption prompt (companies with currently
  // negative FCF and no positive-margin history to glide toward — see
  // needs_target_margin on the DcfResult) ──
  const [targetMarginPct, setTargetMarginPct] = useState<number>(15);
  const [marginGlideYearsInput, setMarginGlideYearsInput] = useState<number>(7);
  const [marginSubmitting, setMarginSubmitting] = useState(false);
  const [marginSubmitError, setMarginSubmitError] = useState<string | null>(null);

  const runGrowthDcf = () => {
    setMarginSubmitting(true);
    setMarginSubmitError(null);
    recomputeDcf(symbol, { target_fcf_margin: targetMarginPct / 100, margin_glide_years: marginGlideYearsInput })
      .then(d => {
        if (d.needs_target_margin) {
          setMarginSubmitError('Still not computable — try a different margin assumption.');
          return;
        }
        baseAssumptions.current = d.assumptions;
        setResult(d);
        setG1(d.assumptions.stage1_growth);
        setTg(d.assumptions.terminal_growth);
        setWacc(d.assumptions.wacc);
        setWaccOverrideOn(false);
        setBaseFcfB((d.assumptions.base_fcf / 1e9).toFixed(1));
      })
      .catch(e => setMarginSubmitError(e?.response?.data?.detail || 'Could not compute with this assumption.'))
      .finally(() => setMarginSubmitting(false));
  };

  // ── Multiples-implied target: linked to the DCF's Stage-1 growth (g1) so it
  // moves with the same slider/Bear/Base/Bull scenario instead of sitting on
  // a disconnected, fixed growth assumption. Waits for g1 (set once the DCF
  // base case loads) and debounces the same way the DCF recompute does. ──
  useEffect(() => {
    // DCF hasn't resolved at all yet — nothing to link to and nothing to
    // show independently of it either. Once it resolves (even as a
    // needs_target_margin partial), the Multiples card should still render
    // on its own — it doesn't depend on the DCF succeeding.
    if (g1 == null && !result?.needs_target_margin) return;
    if (multiplesDebounceRef.current) clearTimeout(multiplesDebounceRef.current);
    let cancelled = false;
    multiplesDebounceRef.current = setTimeout(() => {
      setMultiplesLoading(true);
      getMultiplesTarget(symbol, g1 ?? undefined)
        .then(d => !cancelled && setMultiples(d))
        .catch(() => !cancelled && setMultiples(null))
        .finally(() => !cancelled && setMultiplesLoading(false));
    }, 250);
    return () => { cancelled = true; if (multiplesDebounceRef.current) clearTimeout(multiplesDebounceRef.current); };
    // eslint-disable-next-line
  }, [symbol, g1, result?.needs_target_margin]);

  // ── Build current overrides from edited state ──
  const currentOverrides = useCallback((): DcfOverrides => {
    const base = baseAssumptions.current;
    const ov: DcfOverrides = {};
    if (g1 != null && base && Math.abs(g1 - base.stage1_growth) > 1e-6) ov.stage1_growth = g1;
    if (tg != null && base && Math.abs(tg - base.terminal_growth) > 1e-6) ov.terminal_growth = tg;
    if (waccOverrideOn && wacc != null) ov.wacc = wacc;
    const fcfNum = parseFloat(baseFcfB) * 1e9;
    if (base && Number.isFinite(fcfNum) && fcfNum > 0 && Math.abs(fcfNum - base.base_fcf) / base.base_fcf > 0.001) {
      ov.base_fcf = fcfNum;
    }
    return ov;
  }, [g1, tg, wacc, waccOverrideOn, baseFcfB]);

  // ── Recompute now (used by both the slider debounce below and the Base
  // FCF text field's onBlur/Enter — a free-text numeric input shouldn't
  // recompute after every keystroke pause the way a dragged slider does,
  // that reads as the whole panel "auto-reloading" while you're mid-edit). ──
  const recomputeNow = useCallback(() => {
    if (loading || !baseAssumptions.current) return;
    const ov = currentOverrides();
    setRecomputing(true);
    recomputeDcf(symbol, ov)
      .then(d => setResult(d))
      .catch(() => {})
      .finally(() => setRecomputing(false));
  }, [loading, symbol, currentOverrides]);

  // ── Debounced recompute when SLIDER assumptions change. baseFcfB is
  // deliberately NOT a dependency here — it recomputes on blur/Enter
  // instead (see the TextField below), not on every keystroke pause. ──
  useEffect(() => {
    if (loading || !baseAssumptions.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(recomputeNow, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line
  }, [g1, tg, wacc, waccOverrideOn]);

  // ── Reverse DCF on demand ──
  useEffect(() => {
    if (mode !== 'reverse' || loading) return;
    setReverseLoading(true);
    const ov = currentOverrides();
    delete ov.stage1_growth;  // reverse solves for growth; can't also override it
    getReverseDcf(symbol, undefined, ov)
      .then(setReverse)
      .catch(() => setReverse(null))
      .finally(() => setReverseLoading(false));
    // eslint-disable-next-line
  }, [mode, symbol, loading, tg, wacc, waccOverrideOn, baseFcfB]);

  // ── Scenarios (localStorage, with computed presets as a working default) ──
  // Bear/Base/Bull used to start out completely empty — clicking one before
  // you'd ever shift-clicked to save something did nothing at all, silently.
  // Now each name has a sensible preset derived from this stock's own base
  // case, so the buttons work immediately; shift-click still lets you save
  // your own tuned assumptions over the preset, per-symbol.
  const [activeScenario, setActiveScenario] = useState<ScenarioName | null>(null);
  const [scenarioVersion, setScenarioVersion] = useState(0);

  const computePreset = (name: ScenarioName) => {
    const b = baseAssumptions.current;
    if (!b) return null;
    const baseFcfB = (b.base_fcf / 1e9).toFixed(1);
    if (name === 'base') {
      return { g1: b.stage1_growth, tg: b.terminal_growth, waccOverrideOn: false, wacc: b.wacc, baseFcfB };
    }
    if (name === 'bear') {
      // A deceleration off consensus, not a collapse: halving+ growth AND
      // penalizing WACC at the same time double-counts pessimism and can
      // land somewhere absurd (e.g. anchoring to a temporarily depressed
      // historical FCF CAGR during a heavy capex cycle). Bear = consensus
      // growth cut by ~45%, terminal growth trimmed slightly, WACC up 1pt.
      return {
        g1: Math.max(0, b.stage1_growth * 0.55), tg: Math.max(0.01, b.terminal_growth - 0.0025),
        waccOverrideOn: true, wacc: Math.min(0.16, b.wacc + 0.01), baseFcfB,
      };
    }
    // bull: consensus growth stretched ~25% (capped at 35%), terminal growth
    // nudged up, WACC down 0.75pt — a re-rating case, not a fantasy one.
    return {
      g1: Math.min(0.35, b.stage1_growth * 1.25), tg: Math.min(0.04, b.terminal_growth + 0.0025),
      waccOverrideOn: true, wacc: Math.max(0.04, b.wacc - 0.0075), baseFcfB,
    };
  };

  const saveScenario = (name: ScenarioName) => {
    try {
      const all = JSON.parse(localStorage.getItem(scenarioKey(symbol)) || '{}');
      all[name] = { g1, tg, waccOverrideOn, wacc, baseFcfB };
      localStorage.setItem(scenarioKey(symbol), JSON.stringify(all));
      setActiveScenario(name);
      setScenarioVersion(v => v + 1); // force savedScenarios/chip highlight to refresh now
    } catch { /* ignore */ }
  };
  const loadScenario = (name: ScenarioName) => {
    try {
      const all = JSON.parse(localStorage.getItem(scenarioKey(symbol)) || '{}');
      const s = all[name] || computePreset(name);
      if (!s) return false;
      setG1(s.g1); setTg(s.tg); setWaccOverrideOn(s.waccOverrideOn); setWacc(s.wacc); setBaseFcfB(s.baseFcfB);
      setActiveScenario(name);
      return true;
    } catch { return false; }
  };
  const savedScenarios: Record<string, boolean> = useMemo(() => {
    try {
      const all = JSON.parse(localStorage.getItem(scenarioKey(symbol)) || '{}');
      return { bear: !!all.bear, base: !!all.base, bull: !!all.bull };
    } catch { return { bear: false, base: false, bull: false }; }
    // eslint-disable-next-line
  }, [symbol, result, scenarioVersion]);

  const resetToBase = () => {
    const b = baseAssumptions.current;
    if (!b) return;
    setG1(b.stage1_growth); setTg(b.terminal_growth);
    setWaccOverrideOn(false); setWacc(b.wacc);
    setBaseFcfB((b.base_fcf / 1e9).toFixed(1));
    setActiveScenario(null);
  };

  // ── Render ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320, flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>
          Building DCF model (fetching financials)…
        </Typography>
      </Box>
    );
  }
  if (error || !result) {
    return <Alert severity="info">{error || 'No valuation available.'}</Alert>;
  }

  // Terminal Value breakdown — how much of the fair value rests on the
  // terminal-year assumption vs. the explicit forecast, an implied exit
  // multiple to sanity-check the terminal value against real trading
  // multiples, and the WACC−terminal-growth spread (a thin spread makes the
  // Gordon Growth formula highly sensitive to small assumption changes).
  // Shared between the standard DCF and Growth DCF cards — same shape,
  // different terminal-year cash flow definition underneath.
  const renderTerminalBreakdown = (tvb: TerminalValueBreakdown) => {
    const tvPct = tvb.terminal_value_pct_of_ev ?? 0;
    const explicitPct = tvb.explicit_fcf_pct_of_ev ?? 0;
    const heavyTerminal = tvb.terminal_value_pct_of_ev != null && tvb.terminal_value_pct_of_ev > 75;
    const thinSpread = tvb.wacc_terminal_spread < 1.5;
    return (
      <Paper elevation={0} sx={CARD_SX}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
          Terminal Value Breakdown
        </Typography>

        {/* Explicit vs. terminal split — stacked bar */}
        <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', mb: 1 }}>
          <Box sx={{ width: `${explicitPct}%`, bgcolor: '#3b82f6' }} />
          <Box sx={{ width: `${tvPct}%`, bgcolor: bloomberg.accent.orange }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
            <span style={{ color: '#3b82f6' }}>■</span> Explicit forecast: <b style={{ color: bloomberg.text.secondary }}>{tvb.explicit_fcf_pct_of_ev ?? '—'}%</b> ({tvb.pv_explicit_fcf})
          </Typography>
          <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
            <span style={{ color: bloomberg.accent.orange }}>■</span> Terminal value: <b style={{ color: bloomberg.text.secondary }}>{tvb.terminal_value_pct_of_ev ?? '—'}%</b> ({tvb.pv_terminal_value})
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {[
            ['Terminal-year FCF', tvb.terminal_year_fcf],
            ['Terminal value (undiscounted)', tvb.terminal_value_raw],
            ['Implied terminal multiple', tvb.implied_terminal_multiple != null ? `${tvb.implied_terminal_multiple}x` : 'n/m'],
            ['WACC − terminal growth', `${tvb.wacc_terminal_spread.toFixed(2)}pp`],
          ].map(([k, v]) => (
            <Box key={k}>
              <Typography sx={{ fontSize: 9, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k}</Typography>
              <Typography sx={{ fontSize: 13, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.primary }}>{v}</Typography>
            </Box>
          ))}
        </Box>

        {tvb.implied_terminal_multiple == null && (
          <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, mt: 1.5 }}>
            No implied multiple shown — terminal-year cash flow is still negative, so a TV ÷ FCF multiple isn't meaningful.
          </Typography>
        )}
        {heavyTerminal && (
          <Typography sx={{ fontSize: 10, color: bloomberg.accent.orange, mt: 1.5 }}>
            ⚠ Over 75% of this valuation comes from the terminal assumption, not the 10-year explicit forecast — the fair
            value is mostly a bet on what happens in perpetuity, not on anything projectable in the near term.
          </Typography>
        )}
        {thinSpread && (
          <Typography sx={{ fontSize: 10, color: bloomberg.accent.orange, mt: 1 }}>
            ⚠ WACC and terminal growth are only {tvb.wacc_terminal_spread.toFixed(2)} points apart — the Gordon Growth
            denominator (WACC − g) is thin here, so the terminal value (and fair value) is unusually sensitive to small
            changes in either slider.
          </Typography>
        )}
      </Paper>
    );
  };

  // Multiples-Implied Target card — independent of whether the DCF itself
  // could compute (e.g. a company with no positive-FCF history to anchor a
  // DCF may still have a perfectly good EV/Revenue-based target), so it's
  // built once here and rendered from both the normal path and the
  // needs-target-margin path below.
  const multiplesCard = (
    <Paper elevation={0} sx={CARD_SX}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Multiples-Implied Target
          </Typography>
          {multiples?.data_source && (
            <Box
              component="span"
              sx={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.05em',
                px: 0.75,
                py: 0.15,
                borderRadius: '3px',
                textTransform: 'uppercase',
                color: multiples.data_source === 'edgar' ? bloomberg.status.positive : bloomberg.text.muted,
                border: `1px solid ${multiples.data_source === 'edgar' ? bloomberg.status.positive : bloomberg.text.muted}`,
              }}
            >
              {multiples.data_source === 'edgar'
                ? `SEC EDGAR · ${Math.max(multiples.pe_years_used, multiples.ev_ebitda_years_used, multiples.ev_revenue_years_used)}yr`
                : `yfinance · ${Math.max(multiples.pe_years_used, multiples.ev_ebitda_years_used, multiples.ev_revenue_years_used)}yr`}
            </Box>
          )}
        </Box>
        {multiples?.growth_source && (
          <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
            growth: {pct(multiples.implied_ebitda_growth)} ({multiples.growth_source})
          </Typography>
        )}
      </Box>
      {multiplesLoading ? (
        <CircularProgress size={20} sx={{ color: bloomberg.accent.orange }} />
      ) : !multiples || (multiples.pe_implied_price == null && multiples.ev_ebitda_implied_price == null && multiples.ev_revenue_implied_price == null) ? (
        <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>
          Not enough historical multiple data for {symbol}.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Blended target
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.primary, lineHeight: 1.1 }}>
                {multiples.blended_target != null ? `${currencySymbol}${multiples.blended_target.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
              </Typography>
            </Box>
            {multiples.blended_upside != null && (
              <Box>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>vs price {currencySymbol}{multiples.current_price.toLocaleString()}</Typography>
                <Typography sx={{ fontSize: 17, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: multiples.blended_upside >= 0 ? bloomberg.status.positive : bloomberg.status.negative }}>
                  {multiples.blended_upside >= 0 ? '▲ +' : '▼ '}{multiples.blended_upside.toFixed(1)}%
                </Typography>
              </Box>
            )}
            {mode === 'dcf' && !result.needs_target_margin && (
              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>vs DCF fair value</Typography>
                <Typography sx={{ fontSize: 13, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.secondary }}>
                  {currencySymbol}{result.fair_value_per_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({((result.fair_value_per_share - result.current_price) / result.current_price * 100) >= 0 ? '+' : ''}{((result.fair_value_per_share - result.current_price) / result.current_price * 100).toFixed(1)}%)
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 200px' }}>
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, mb: 0.25 }}>
                P/E method {multiples.pe_years_used > 0 && `(${multiples.pe_years_used}yr avg)`}
              </Typography>
              {multiples.pe_implied_price != null ? (
                <>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: (multiples.pe_upside ?? 0) >= 0 ? bloomberg.status.positive : bloomberg.status.negative }}>
                    {currencySymbol}{multiples.pe_implied_price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    <span style={{ fontSize: 11, color: bloomberg.text.muted, fontWeight: 400 }}> ({multiples.pe_upside! >= 0 ? '+' : ''}{multiples.pe_upside}%)</span>
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
                    1yr-fwd EPS {currencySymbol}{multiples.forward_eps} × hist avg {multiples.hist_avg_pe}x (now {multiples.current_pe}x)
                    {multiples.analyst_forward_eps != null && ` · analyst consensus fwd EPS: ${currencySymbol}${multiples.analyst_forward_eps}`}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>Not available</Typography>
              )}
            </Box>
            <Box sx={{ flex: '1 1 200px' }}>
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, mb: 0.25 }}>
                EV/EBITDA method {multiples.ev_ebitda_years_used > 0 && `(${multiples.ev_ebitda_years_used}yr avg)`}
              </Typography>
              {multiples.ev_ebitda_implied_price != null ? (
                <>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: (multiples.ev_ebitda_upside ?? 0) >= 0 ? bloomberg.status.positive : bloomberg.status.negative }}>
                    {currencySymbol}{multiples.ev_ebitda_implied_price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    <span style={{ fontSize: 11, color: bloomberg.text.muted, fontWeight: 400 }}> ({multiples.ev_ebitda_upside! >= 0 ? '+' : ''}{multiples.ev_ebitda_upside}%)</span>
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
                    hist avg {multiples.hist_avg_ev_ebitda}x (now {multiples.current_ev_ebitda ?? '—'}x), EBITDA grown {pct(multiples.implied_ebitda_growth)}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>Not available</Typography>
              )}
            </Box>
            <Box sx={{ flex: '1 1 200px' }}>
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, mb: 0.25 }}>
                EV/Revenue method {multiples.ev_revenue_years_used > 0 && `(${multiples.ev_revenue_years_used}yr avg)`}
              </Typography>
              {multiples.ev_revenue_implied_price != null ? (
                <>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: (multiples.ev_revenue_upside ?? 0) >= 0 ? bloomberg.status.positive : bloomberg.status.negative }}>
                    {currencySymbol}{multiples.ev_revenue_implied_price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    <span style={{ fontSize: 11, color: bloomberg.text.muted, fontWeight: 400 }}> ({multiples.ev_revenue_upside! >= 0 ? '+' : ''}{multiples.ev_revenue_upside}%)</span>
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
                    hist avg {multiples.hist_avg_ev_revenue}x (now {multiples.current_ev_revenue ?? '—'}x) — doesn't require positive earnings
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>Not available</Typography>
              )}
            </Box>
          </Box>

          {(multiples.pe_years_used > 0 && multiples.pe_years_used < 4) && (
            <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, mt: 1.5 }}>
              ⚠ Based on only {Math.max(multiples.pe_years_used, multiples.ev_ebitda_years_used, multiples.ev_revenue_years_used)} years of annual data
              {multiples.data_source === 'yfinance' ? ' (no SEC EDGAR coverage for this symbol, likely non-US-listed)' : ''}
              — treat as directional, not precise. Mean-reversion to a stock's own multiple also assumes the market
              re-rates it back to historical norms, which isn't guaranteed if the business or its growth profile
              has genuinely changed.
            </Typography>
          )}
        </>
      )}
    </Paper>
  );

  // Mode switcher — defined once here so it's reachable from both the
  // needs_target_margin branch below and the main render, without
  // duplicating the ToggleButtonGroup JSX.
  const modeSwitcher = (
    <ToggleButtonGroup
      value={mode} exclusive size="small"
      onChange={(_, v) => v && setMode(v)}
      sx={{ '& .MuiToggleButton-root': { py: 0.3, px: 1.5, fontSize: '0.72rem' } }}
    >
      <ToggleButton value="dcf">DCF</ToggleButton>
      <ToggleButton value="reverse">Reverse DCF</ToggleButton>
      <ToggleButton value="growth">Growth DCF</ToggleButton>
      <ToggleButton value="relative">Peer Regression</ToggleButton>
    </ToggleButtonGroup>
  );

  // ── Growth DCF mode content — Damodaran-style (peer-margin convergence +
  // sales-to-capital reinvestment). Independent of `result`/`a` (the
  // standard DCF's own state) by design — own sliders, own fetch. ──
  const growthModeContent = (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 }}>
      <Paper elevation={0} sx={CARD_SX}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
          Growth DCF Assumptions
        </Typography>
        <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, mb: 2, lineHeight: 1.5 }}>
          Revenue-driven: operating margin converges to a peer-derived target (not {symbol}'s own history),
          reinvestment comes from a sales-to-capital ratio. A separate engine from the DCF tab — handles
          negative-starting margins without needing a manual target-margin assumption.
        </Typography>
        {growthLoading ? (
          <CircularProgress size={20} sx={{ color: bloomberg.accent.orange }} />
        ) : growthError ? (
          <Alert severity="info" sx={{ fontSize: 12 }}>{growthError}</Alert>
        ) : growthResult ? (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Stage 1 revenue growth (Yrs 1-5)</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>{pct(ggGrowth)}</Typography>
              </Box>
              <Slider value={(ggGrowth ?? 0.1) * 100} onChange={(_, v) => setGgGrowth((v as number) / 100)} min={-10} max={60} step={1} size="small" sx={SLIDER_SX} />
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>base: {pct(growthResult.assumptions.stage1_growth)} ({growthResult.growth_source})</Typography>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Terminal growth</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>{pct(ggTermGrowth)}</Typography>
              </Box>
              <Slider value={(ggTermGrowth ?? 0.025) * 100} onChange={(_, v) => setGgTermGrowth((v as number) / 100)} min={0.5} max={4} step={0.1} size="small" sx={SLIDER_SX} />
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Target operating margin</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>{pct(ggTargetMargin)}</Typography>
              </Box>
              <Slider value={(ggTargetMargin ?? 0.15) * 100} onChange={(_, v) => setGgTargetMargin((v as number) / 100)} min={-20} max={45} step={1} size="small" sx={SLIDER_SX} />
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
                {growthResult.margin_source} · current: {pct(growthResult.assumptions.current_operating_margin)}
              </Typography>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Sales-to-capital ratio</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>{(ggSalesToCapital ?? 2).toFixed(2)}x</Typography>
              </Box>
              <Slider value={ggSalesToCapital ?? 2} onChange={(_, v) => setGgSalesToCapital(v as number)} min={0.1} max={6} step={0.1} size="small" sx={SLIDER_SX} />
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>{growthResult.sales_to_capital_source} — revenue $ generated per $ reinvested</Typography>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Years to reach target margin</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>{ggConvergenceYears}</Typography>
              </Box>
              <Slider value={ggConvergenceYears} onChange={(_, v) => setGgConvergenceYears(v as number)} min={3} max={10} step={1} size="small" sx={SLIDER_SX} />
            </Box>

            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                  control={<Switch size="small" checked={ggWaccOverrideOn} onChange={e => setGgWaccOverrideOn(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>WACC override</Typography>}
                  sx={{ ml: -0.5 }}
                />
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: ggWaccOverrideOn ? bloomberg.accent.orange : bloomberg.text.primary }}>
                  {pct(ggWaccOverrideOn ? ggWacc : growthResult.assumptions.wacc, 2)}
                </Typography>
              </Box>
              {ggWaccOverrideOn && (
                <Slider value={(ggWacc ?? 0.09) * 100} onChange={(_, v) => setGgWacc((v as number) / 100)} min={4} max={16} step={0.1} size="small" sx={SLIDER_SX} />
              )}
            </Box>

            <Chip
              label="RESET" size="small" clickable onClick={resetGrowthToBase}
              sx={{ fontSize: '0.65rem', height: 24, bgcolor: 'transparent', color: bloomberg.text.muted, border: `1px solid ${bloomberg.border.primary}` }}
            />
            {growthRecomputing && <CircularProgress size={14} sx={{ color: bloomberg.accent.orange, ml: 1 }} />}
          </>
        ) : null}
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {growthResult && (
          <>
            <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', alignItems: 'baseline', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Fair value (Growth DCF)
                </Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.primary, lineHeight: 1.1 }}>
                  {currencySymbol}{growthResult.fair_value_per_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>vs price {currencySymbol}{growthResult.current_price.toLocaleString()}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: growthResult.fair_value_per_share >= growthResult.current_price ? bloomberg.status.positive : bloomberg.status.negative }}>
                  {growthResult.upside_downside}
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                {[
                  ['EV', growthResult.enterprise_value],
                  ['− Net debt', growthResult.net_debt],
                  ['= Equity', growthResult.equity_value],
                  ['÷ Shares', growthResult.shares_outstanding],
                ].map(([k, v]) => (
                  <Box key={k as string} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>{k}</Typography>
                    <Typography sx={{ fontSize: 10, color: bloomberg.text.secondary, fontFamily: '"IBM Plex Mono", monospace', minWidth: 70, textAlign: 'right' }}>{v}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            {renderTerminalBreakdown(growthResult.terminal_value_breakdown)}

            <Paper elevation={0} sx={CARD_SX}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                Projected FCFF (10y, {growthResult.financial_currency})
              </Typography>
              <ResponsiveContainer width="100%" height={130}>
                <BarChart
                  data={growthResult.projected_fcf.map(p => ({ year: String(p.year).slice(2), fcf: parseFloat(String(p.fcf).replace(/[^0-9.\-]/g, '')) || 0 }))}
                  margin={{ top: 4, right: 4, bottom: 0, left: 4 }}
                >
                  <XAxis dataKey="year" tick={{ fill: bloomberg.text.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <ReTooltip
                    contentStyle={{ background: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.secondary}`, fontSize: '0.7rem' }}
                    formatter={(v: any) => [`${Number(v).toFixed(1)}B`, 'FCFF']}
                  />
                  <Bar dataKey="fcf" radius={[2, 2, 0, 0]}>
                    {growthResult.projected_fcf.map((_, i) => (
                      <Cell key={i} fill={i < 5 ? bloomberg.accent.orange : '#3b82f6'} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Typography sx={{ fontSize: 9, color: bloomberg.text.muted, mt: 0.5 }}>
                Margin: {growthResult.projected_fcf[0]?.margin} → {growthResult.projected_fcf[4]?.margin} → {growthResult.projected_fcf[9]?.margin}
              </Typography>
            </Paper>

            <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, lineHeight: 1.5 }}>{growthResult.methodology}</Typography>
          </>
        )}
      </Box>
    </Box>
  );

  // ── Peer Regression mode content — EV/Revenue vs. forward growth across a
  // peer group. Independent of `result`/`a` by design. ──
  const peerModeContent = (
    <Box>
      {peerLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={20} sx={{ color: bloomberg.accent.orange }} /></Box>
      ) : peerError ? (
        <Alert severity="info" sx={{ fontSize: 12 }}>{peerError}</Alert>
      ) : !peerResult ? null : peerResult.insufficient_peer_data ? (
        <Alert severity="info" sx={{ fontSize: 12 }}>
          Not enough peer data for {symbol} ({peerResult.n_peers_found ?? 0} usable peers found, need at least 4)
          {peerResult.sector && ` — sector: ${peerResult.sector}`}{peerResult.industry && `, industry: ${peerResult.industry}`}.
        </Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 }}>
          <Paper elevation={0} sx={CARD_SX}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
              Peer Regression Assumptions
            </Typography>
            <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, mb: 2, lineHeight: 1.5 }}>
              EV/Revenue regressed against forward growth across {peerResult.n_peers} {peerResult.industry || peerResult.sector || 'peer'} companies —
              reads off the fitted multiple at the growth rate below, instead of {symbol}'s own historical multiple.
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Target revenue growth</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>
                  {(peerGrowthPct ?? 0).toFixed(1)}%
                </Typography>
              </Box>
              <Slider
                value={peerGrowthPct ?? 0}
                onChange={(_, v) => setPeerGrowthPct(v as number)}
                min={Math.min(-20, Math.floor(peerResult.peer_growth_range[0] * 100))}
                max={Math.max(80, Math.ceil(peerResult.peer_growth_range[1] * 100))}
                step={1} size="small" sx={SLIDER_SX}
              />
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
                peer range: {(peerResult.peer_growth_range[0] * 100).toFixed(0)}% – {(peerResult.peer_growth_range[1] * 100).toFixed(0)}%
                {' · '}{symbol}'s own trailing growth: {pct(peerResult.target_growth)}
              </Typography>
              {peerResult.is_extrapolated && (
                <Typography sx={{ fontSize: 10, color: bloomberg.accent.orange, mt: 0.5 }}>
                  ⚠ Outside the peer sample's range — extrapolation, not a statistically supported read.
                </Typography>
              )}
            </Box>
            {peerRecomputing && <CircularProgress size={14} sx={{ color: bloomberg.accent.orange }} />}
          </Paper>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', alignItems: 'baseline', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Implied price (peer regression)
                </Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.primary, lineHeight: 1.1 }}>
                  {peerResult.implied_price != null ? `${currencySymbol}${peerResult.implied_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                </Typography>
              </Box>
              {peerResult.upside_downside && (
                <Box>
                  <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>vs price {currencySymbol}{peerResult.current_price?.toLocaleString()}</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: (peerResult.implied_price ?? 0) >= (peerResult.current_price ?? 0) ? bloomberg.status.positive : bloomberg.status.negative }}>
                    {peerResult.upside_downside}
                  </Typography>
                </Box>
              )}
              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
                  Implied EV/Revenue: <b style={{ color: bloomberg.text.secondary }}>{peerResult.implied_ev_revenue_multiple.toFixed(1)}x</b>
                </Typography>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>R² = {peerResult.r_squared.toFixed(2)} · n={peerResult.n_peers}</Typography>
              </Box>
            </Paper>

            <Paper elevation={0} sx={CARD_SX}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                Peer EV/Revenue vs. Growth
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <ScatterChart margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke={bloomberg.border.secondary} strokeDasharray="3 3" />
                  <XAxis
                    type="number" dataKey="growth" name="Growth" tick={{ fill: bloomberg.text.muted, fontSize: 9 }}
                    domain={['dataMin', 'dataMax']} tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                  />
                  <YAxis type="number" dataKey="ev_revenue" name="EV/Revenue" tick={{ fill: bloomberg.text.muted, fontSize: 9 }} />
                  <ReTooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    contentStyle={{ background: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.secondary}`, fontSize: '0.7rem' }}
                    formatter={(v: any, n: any) => [n === 'growth' ? `${(Number(v) * 100).toFixed(1)}%` : `${Number(v).toFixed(1)}x`, n]}
                    labelFormatter={() => ''}
                  />
                  <Scatter name="Peers" data={peerResult.peer_points} fill={bloomberg.text.muted} />
                  <Scatter
                    name={symbol}
                    data={[{ growth: peerResult.target_growth, ev_revenue: peerResult.implied_ev_revenue_multiple }]}
                    fill={bloomberg.accent.orange} shape="star"
                  />
                  <ReferenceLine
                    segment={[
                      { x: peerResult.peer_growth_range[0], y: peerResult.intercept + peerResult.slope * peerResult.peer_growth_range[0] },
                      { x: peerResult.peer_growth_range[1], y: peerResult.intercept + peerResult.slope * peerResult.peer_growth_range[1] },
                    ]}
                    stroke={bloomberg.accent.orange} strokeDasharray="4 4"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </Paper>

            <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, lineHeight: 1.5 }}>{peerResult.methodology}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  // ── DCF needs an explicit mature-margin assumption: currently-negative
  // FCF with no positive-margin history to glide toward (see needs_target_margin
  // on compute_dcf). Show the Multiples card (which doesn't need this) plus
  // an assumption-entry prompt, instead of the normal DCF output. ──
  if (result.needs_target_margin) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {modeSwitcher}
        </Box>
        {mode === 'growth' && growthModeContent}
        {mode === 'relative' && peerModeContent}
        {(mode === 'dcf' || mode === 'reverse') && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 }}>
            <Paper elevation={0} sx={CARD_SX}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
                DCF needs a mature-margin assumption
              </Typography>
              <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary, mb: 2, lineHeight: 1.5 }}>
                {symbol}'s current FCF margin is <b style={{ color: bloomberg.status.negative }}>{pct(result.current_fcf_margin)}</b>
                {result.hist_avg_fcf_margin != null
                  ? <> and its historical average ({pct(result.hist_avg_fcf_margin)}) isn't enough history (needs 3+ profitable years) to project a recovery from.</>
                  : <> with no year of positive FCF margin on record to anchor a recovery projection.</>}
                {' '}Set an assumed mature FCF margin and how many years it takes to get there, and the DCF will model
                revenue growing at the usual schedule while margin glides from today's level to that target.
              </Typography>

              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Target mature FCF margin</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>
                    {targetMarginPct.toFixed(0)}%
                  </Typography>
                </Box>
                <Slider
                  value={targetMarginPct}
                  onChange={(_, v) => setTargetMarginPct(v as number)}
                  min={-10} max={40} step={1}
                  size="small" sx={SLIDER_SX}
                />
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Years to reach it</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>
                    {marginGlideYearsInput}
                  </Typography>
                </Box>
                <Slider
                  value={marginGlideYearsInput}
                  onChange={(_, v) => setMarginGlideYearsInput(v as number)}
                  min={3} max={10} step={1}
                  size="small" sx={SLIDER_SX}
                />
              </Box>

              <Chip
                label={marginSubmitting ? 'Computing…' : 'Run DCF'} size="small" clickable
                onClick={marginSubmitting ? undefined : runGrowthDcf}
                sx={{
                  fontSize: '0.7rem', height: 28, fontWeight: 700, px: 1,
                  bgcolor: bloomberg.accent.orange, color: '#1a0f00',
                }}
              />
              {marginSubmitError && (
                <Typography sx={{ fontSize: 11, color: bloomberg.status.negative, mt: 1 }}>{marginSubmitError}</Typography>
              )}
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {multiplesCard}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  const a = result.assumptions;
  const upsideNum = result.current_price > 0
    ? ((result.fair_value_per_share - result.current_price) / result.current_price) * 100
    : 0;
  const upColor = upsideNum >= 0 ? bloomberg.status.positive : bloomberg.status.negative;

  // FCF chart data (parse year + raw growth from projected list)
  const fcfChart = result.projected_fcf.map(p => ({
    year: String(p.year).slice(2),
    fcf: parseFloat(String(p.fcf).replace(/[^0-9.\-]/g, '')) || 0,
  }));

  // Sensitivity → 5×5 grid keyed by wacc rows / tg cols
  const sensWaccs = Array.from(new Set(result.sensitivity_table.map(s => s.wacc))).sort((x, y) => x - y);
  const sensTgs = Array.from(new Set(result.sensitivity_table.map(s => s.terminal_growth))).sort((x, y) => x - y);
  const sensAt = (w: number, t: number) =>
    result.sensitivity_table.find(s => s.wacc === w && s.terminal_growth === t)?.fair_value;

  return (
    <Box>
      {/* ── Mode + scenarios toolbar ── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {modeSwitcher}

        {(mode === 'dcf' || mode === 'reverse') && <Box sx={{ width: 1, height: 22, bgcolor: bloomberg.border.primary }} />}

        {(mode === 'dcf' || mode === 'reverse') && (['bear', 'base', 'bull'] as ScenarioName[]).map(name => {
          const isActive = activeScenario === name;
          const hasCustomSave = savedScenarios[name];
          return (
            <Tooltip
              key={name}
              title={
                hasCustomSave
                  ? `Click to load your saved ${name.toUpperCase()} · Shift-click to overwrite with current assumptions`
                  : `Click to load a computed ${name.toUpperCase()} preset · Shift-click to save current assumptions as your own ${name.toUpperCase()}`
              }
              arrow
            >
              <Chip
                label={name.toUpperCase()} size="small" clickable
                onClick={(e) => { if (e.shiftKey) saveScenario(name); else loadScenario(name); }}
                sx={{
                  fontSize: '0.65rem', height: 24, fontWeight: 700,
                  bgcolor: isActive ? bloomberg.accent.orange : hasCustomSave ? `${bloomberg.accent.orange}22` : bloomberg.bg.tertiary,
                  color: isActive ? '#1a0f00' : hasCustomSave ? bloomberg.accent.orange : bloomberg.text.muted,
                  border: `1px solid ${hasCustomSave || isActive ? bloomberg.accent.orange : bloomberg.border.primary}`,
                }}
              />
            </Tooltip>
          );
        })}
        {(mode === 'dcf' || mode === 'reverse') && (
          <Chip
            label="RESET" size="small" clickable onClick={resetToBase}
            sx={{ fontSize: '0.65rem', height: 24, bgcolor: 'transparent', color: bloomberg.text.muted, border: `1px solid ${bloomberg.border.primary}` }}
          />
        )}
        {(mode === 'dcf' || mode === 'reverse') && recomputing && <CircularProgress size={14} sx={{ color: bloomberg.accent.orange }} />}
      </Box>

      {mode === 'growth' && growthModeContent}
      {mode === 'relative' && peerModeContent}

      {(mode === 'dcf' || mode === 'reverse') && (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 }}>
        {/* ════ LEFT: Assumptions ════ */}
        <Paper elevation={0} sx={CARD_SX}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2 }}>
            Assumptions
          </Typography>

          {/* Base FCF */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>
                Base FCF {a.use_normalized ? '(normalized)' : '(TTM)'}
              </Typography>
              <TextField
                value={baseFcfB}
                onChange={e => { setBaseFcfB(e.target.value); setActiveScenario(null); }}
                onBlur={recomputeNow}
                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                size="small" variant="outlined"
                InputProps={{
                  endAdornment: <span style={{ fontSize: 11, color: bloomberg.text.muted }}>B {result.financial_currency}</span>,
                  sx: { fontSize: 12, fontFamily: '"IBM Plex Mono", monospace', width: 130 },
                }}
              />
            </Box>
            {a.margin_glide_active && (
              <Typography sx={{ fontSize: 10, color: bloomberg.accent.orange, mt: 0.5 }}>
                FCF margin ({pct(a.current_fcf_margin)}) is modeled gliding to
                {a.target_fcf_margin != null ? ` ${pct(a.target_fcf_margin)}` : ` its historical average (${pct(a.hist_avg_fcf_margin)})`}
                {' '}over {a.margin_glide_years ?? 5} years rather than staying at today's level for 10 years.
              </Typography>
            )}
          </Box>

          {/* Stage-1 growth */}
          <Box sx={{ mb: 2.5, opacity: mode === 'reverse' ? 0.45 : 1, pointerEvents: mode === 'reverse' ? 'none' : 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>
                Stage 1 growth (Yrs 1–5)
                {mode === 'reverse' && ' — solved'}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>
                {pct(mode === 'reverse' ? reverse?.implied_stage1_growth : g1)}
              </Typography>
            </Box>
            <Slider
              value={(g1 ?? 0.05) * 100}
              onChange={(_, v) => { setG1((v as number) / 100); setActiveScenario(null); }}
              min={-10} max={30} step={0.5}
              size="small" sx={SLIDER_SX}
            />
            <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>
              base: {pct(a.stage1_growth)} ({a.growth_source})
              {a.hist_fcf_cagr != null && ` · hist CAGR: ${pct(a.hist_fcf_cagr)}`}
            </Typography>
          </Box>

          {/* Terminal growth */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>Terminal growth</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>
                {pct(tg)}
              </Typography>
            </Box>
            <Slider
              value={(tg ?? 0.025) * 100}
              onChange={(_, v) => { setTg((v as number) / 100); setActiveScenario(null); }}
              min={0.5} max={4} step={0.1}
              size="small" sx={SLIDER_SX}
            />
          </Box>

          {/* WACC */}
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small" checked={waccOverrideOn}
                    onChange={e => { setWaccOverrideOn(e.target.checked); setActiveScenario(null); }}
                  />
                }
                label={<Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>WACC override</Typography>}
                sx={{ ml: -0.5 }}
              />
              <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: waccOverrideOn ? bloomberg.accent.orange : bloomberg.text.primary }}>
                {pct(waccOverrideOn ? wacc : a.wacc, 2)}
              </Typography>
            </Box>
            {waccOverrideOn && (
              <Slider
                value={(wacc ?? 0.09) * 100}
                onChange={(_, v) => { setWacc((v as number) / 100); setActiveScenario(null); }}
                min={4} max={16} step={0.1}
                size="small" sx={SLIDER_SX}
              />
            )}
            {/* WACC build-up (informational) */}
            <Box sx={{ mt: 1, p: 1, bgcolor: bloomberg.bg.tertiary, borderRadius: 0.5 }}>
              {[
                ['Risk-free (10Y, live)', `${result.wacc_components.risk_free_rate}%`],
                ['Beta', `${result.wacc_components.beta}`],
                ['ERP', `${result.wacc_components.equity_risk_premium}%`],
                ['Cost of equity', `${result.wacc_components.cost_of_equity}%`],
                ['Cost of debt (a.t.)', `${result.wacc_components.cost_of_debt_aftertax}%`],
                ['Weights E/D', `${result.wacc_components.equity_weight}% / ${result.wacc_components.debt_weight}%`],
              ].map(([k, v]) => (
                <Box key={k as string} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>{k}</Typography>
                  <Typography sx={{ fontSize: 10, color: bloomberg.text.secondary, fontFamily: '"IBM Plex Mono", monospace' }}>{v}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* ════ RIGHT: Output ════ */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Fair value header OR reverse-DCF verdict */}
          {mode === 'dcf' ? (
            <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', alignItems: 'baseline', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Fair value
                </Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.primary, lineHeight: 1.1 }}>
                  {currencySymbol}{result.fair_value_per_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>vs price {currencySymbol}{result.current_price.toLocaleString()}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: upColor }}>
                  {upsideNum >= 0 ? '▲ +' : '▼ '}{upsideNum.toFixed(1)}%
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                {[
                  ['EV', result.enterprise_value],
                  ['− Net debt', result.net_debt],
                  ['= Equity', result.equity_value],
                  ['÷ Shares', result.shares_outstanding],
                ].map(([k, v]) => (
                  <Box key={k as string} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>{k}</Typography>
                    <Typography sx={{ fontSize: 10, color: bloomberg.text.secondary, fontFamily: '"IBM Plex Mono", monospace', minWidth: 70, textAlign: 'right' }}>{v}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          ) : (
            <Paper elevation={0} sx={CARD_SX}>
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                Market-implied growth
              </Typography>
              {reverseLoading ? (
                <CircularProgress size={20} sx={{ color: bloomberg.accent.orange }} />
              ) : reverse?.solvable && reverse.implied_stage1_growth != null ? (
                <>
                  <Typography sx={{ fontSize: 13, color: bloomberg.text.secondary, mb: 0.5 }}>
                    At {currencySymbol}{reverse.target_price.toLocaleString()}, the market is pricing in
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange, lineHeight: 1.1, mb: 1 }}>
                    {pct(reverse.implied_stage1_growth)} <span style={{ fontSize: 14, color: bloomberg.text.muted, fontWeight: 400 }}>FCF growth / yr for 5 years</span>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
                      Analyst base: <b style={{ color: bloomberg.text.secondary }}>{pct(reverse.base_stage1_growth)}</b> ({reverse.growth_source})
                    </Typography>
                    {reverse.hist_fcf_cagr != null && (
                      <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
                        Historical FCF CAGR: <b style={{ color: bloomberg.text.secondary }}>{pct(reverse.hist_fcf_cagr)}</b>
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 11, mt: 1.5, color: reverse.implied_stage1_growth > (reverse.base_stage1_growth ?? 0) ? bloomberg.status.negative : bloomberg.status.positive }}>
                    {reverse.implied_stage1_growth > (reverse.base_stage1_growth ?? 0)
                      ? '⚠ Market expects MORE growth than the analyst base case — priced for optimism.'
                      : '✓ Market expects LESS growth than the analyst base case — margin of safety if base case holds.'}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>
                  Not solvable within −50%…+150% growth at current assumptions.
                </Typography>
              )}
            </Paper>
          )}

          {renderTerminalBreakdown(result.terminal_value_breakdown)}

          {/* Multiples-implied target — a market-anchored cross-check next to
              the pure cash-flow DCF fair value above. Linked to the same
              Stage-1 growth (slider or Bear/Base/Bull) as the DCF, so the two
              move together on growth — they're still expected to disagree on
              the multiple/discounting side, that's the point. Defined once
              above (multiplesCard) so it also renders on the needs-target-
              margin path, which has no DCF fair value to link to. */}
          {multiplesCard}

          {/* Projected FCF chart */}
          <Paper elevation={0} sx={CARD_SX}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
              Projected FCF (10y, {result.financial_currency})
            </Typography>
            <ResponsiveContainer width="100%" height={130}>
              <BarChart data={fcfChart} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <XAxis dataKey="year" tick={{ fill: bloomberg.text.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <ReTooltip
                  contentStyle={{ background: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.secondary}`, fontSize: '0.7rem' }}
                  formatter={(v: any) => [`${Number(v).toFixed(1)}B`, 'FCF']}
                />
                <Bar dataKey="fcf" radius={[2, 2, 0, 0]}>
                  {fcfChart.map((_, i) => (
                    <Cell key={i} fill={i < 5 ? bloomberg.accent.orange : '#3b82f6'} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <Typography sx={{ fontSize: 9, color: bloomberg.text.muted }}>
              <span style={{ color: bloomberg.accent.orange }}>■</span> Stage 1 ({pct(mode === 'reverse' && reverse?.implied_stage1_growth != null ? reverse.implied_stage1_growth : g1)})
              {'  '}<span style={{ color: '#3b82f6' }}>■</span> Stage 2 taper → {pct(tg)}
            </Typography>
          </Paper>

          {/* Sensitivity grid */}
          {mode === 'dcf' && (
            <Paper elevation={0} sx={CARD_SX}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                Sensitivity — fair value by WACC × terminal growth
              </Typography>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: '"IBM Plex Mono", monospace' }}>
                <thead>
                  <tr>
                    <th style={{ fontSize: 9, color: bloomberg.text.muted, padding: 4, textAlign: 'left' }}>WACC ↓ / TG →</th>
                    {sensTgs.map(t => (
                      <th key={t} style={{ fontSize: 9, color: bloomberg.text.muted, padding: 4, textAlign: 'right' }}>{t}%</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sensWaccs.map(w => (
                    <tr key={w}>
                      <td style={{ fontSize: 10, color: bloomberg.text.muted, padding: 4 }}>{w}%</td>
                      {sensTgs.map(t => {
                        const fv = sensAt(w, t);
                        const up = fv != null && result.current_price > 0 ? (fv - result.current_price) / result.current_price : 0;
                        const bg = fv == null ? 'transparent'
                          : up >= 0 ? `rgba(34,197,94,${Math.min(Math.abs(up), 0.5) * 0.55 + 0.06})`
                          : `rgba(239,68,68,${Math.min(Math.abs(up), 0.5) * 0.55 + 0.06})`;
                        return (
                          <td key={t} style={{ fontSize: 10, padding: 4, textAlign: 'right', background: bg, color: bloomberg.text.primary }}>
                            {fv != null ? fv.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <Typography sx={{ fontSize: 9, color: bloomberg.text.muted, mt: 0.5 }}>
                Green = above current price · Red = below. Centre row/col ≈ base case.
              </Typography>
            </Paper>
          )}

          {/* Methodology */}
          <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, lineHeight: 1.5 }}>
            {result.methodology}
          </Typography>
        </Box>
      </Box>
      )}
    </Box>
  );
};

export default ValuationTab;
